import { twMerge } from 'tailwind-merge';
import clsx, { ClassValue } from 'clsx';
import { KeyboardEvent } from 'react';
import Decimal from 'decimal.js';
import { EnumLike } from 'zod';

import { PARAMS_DISALLOWED_CHARACTERS } from '@lib/constants/regex';
import { BASE_URL } from '@lib/constants/env';
import { Params } from '@lib/types';

/**
 * mergeClassNames merges class names conditionally, using `clsx` to handle conditional logic
 * and `twMerge` to merge Tailwind classes correctly, avoiding duplication
 * of conflicting utility classes.
 *
 * @param {ClassValue[]} inputs - List of class names or conditions for class names.
 * @returns {string} - The final merged class name string.
 *
 * Example:
 * mergeClassNames('text-lg', isActive && 'text-bold', 'text-center');
 * // Returns 'text-lg text-center' (if `isActive` is false)
 */
export function mergeClassNames(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Compares two objects to determine if they are deeply equal.
 *
 * @param a - The first object to compare.
 * @param b - The second object to compare.
 * @returns A boolean indicating whether the two objects are equal (true if equal, false otherwise).
 */
export function areObjectsEqual(a: object, b: object): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  if (aKeys.length !== bKeys.length) return false;

  return aKeys.every((rawKey) => {
    const key = rawKey as keyof typeof a;
    return a[key] === b[key];
  });
}

const ALLOWEDKEYS: Array<KeyboardEvent['key']> = [
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'Tab',
];
/**
 * Handles key events for numeric input validation with comprehensive restrictions.
 *
 * @param {KeyboardEvent<HTMLInputElement>} event - The keyboard event from the input
 *
 * Key validation rules:
 * - Allows predefined keys from `ALLOWEDKEYS` (see the code for current values).
 * - Permits standard control keys (Ctrl+A, Ctrl+X)
 * - Prevents arrow key interactions
 * - Restricts multiple decimal points
 * - Blocks non-numeric characters
 * - Limits negative sign placement
 *
 * @example
 * <input
 *   type="text"
 *   onKeyDown={handleKeyDown}
 *   placeholder="Enter numeric value"
 * />
 */
export function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
  const value = event.currentTarget.value;

  // allow keys that are included in the array
  if (ALLOWEDKEYS.includes(event.key)) {
    return;
  }

  // Handle Ctrl + A (Select All)
  if (event.ctrlKey && event.key === 'a') {
    return;
  }

  // Handle Ctrl + X (Cut)
  if (event.ctrlKey && event.key === 'x') {
    return;
  }

  // prevent arrow keys (increment/decrement) if not covered above
  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
    event.preventDefault();
    return;
  }

  // prevent multiple dots
  if (event.key === '.' && value.includes('.')) {
    event.preventDefault();
    return;
  }

  // prevent invalid characters like letters or special symbols
  if (!/[\d.-]/.test(event.key)) {
    event.preventDefault();
    return;
  }

  // prevent multiple negative signs
  if (event.key === '-' && value.includes('-')) {
    event.preventDefault();
    return;
  }

  // prevent a negative sign in positions other than the start
  if (event.key === '-' && event.currentTarget.selectionStart !== 0) {
    event.preventDefault();
    return;
  }
}

interface ProcessParameters {
  integerFilters?: string[];
  floatFilters?: string[];
  booleanFilters?: string[];
  arrayFilters?: string[];
}

/**
 * Processes the given parameters by converting specified keys.
 *
 * - `integerFilters`: Converts values to integers.
 * - `floatFilters`: Converts values to floats.
 * - `booleanFilters`: Converts values to booleans.
 * - `arrayFilters`: Converts values to arrays of numbers.
 *
 * @param params - The key-value pairs to process.
 * @param integerFilters - Keys to convert to integers.
 * @param floatFilters - Keys to convert to floats.
 * @param booleanFilters - Keys to convert to booleans.
 * @param arrayFilters - Keys to convert to arrays of numbers.
 * @returns The modified parameters.
 */
export function processParameters(
  params: URLSearchParams,
  {
    integerFilters,
    floatFilters,
    booleanFilters,
    arrayFilters,
  }: Readonly<ProcessParameters>,
): Params {
  const processedParams: Params = {};

  for (const [key, value] of Object.entries(Object.fromEntries(params))) {
    if (integerFilters?.includes(key)) {
      processedParams[key] = parseInt(value);
    } else if (floatFilters?.includes(key)) {
      processedParams[key] = parseFloat(value);
    } else if (booleanFilters?.includes(key)) {
      processedParams[key] = value === 'true';
    } else if (arrayFilters?.includes(key)) {
      if (!Array.isArray(processedParams[key])) {
        processedParams[key] = [];
      }
      params
        .getAll(key)
        .forEach((type) =>
          (processedParams[key] as number[]).push(parseInt(type)),
        );
    } else {
      // As a fallback, pass string values directly to the process parameters
      processedParams[key] = value;
    }
  }

  return processedParams;
}

/**
 * A mapped type that extracts the field names of an object.
 * This ensures that for every key `K` in `T`, the value is the key itself.
 *
 * Example:
 * ```
 * type Example = { id: number; name: string };
 * type Extracted = FieldNameExtractor<Example>;
 * // Extracted will be: { id: "id"; name: "name" }
 * ```
 */
type FieldNameExtractor<T> = {
  [K in keyof T]: K;
};

/**
 * Utility function to create a proxy that returns field names instead of their values.
 *
 * @param obj - The object whose field names need to be extracted.
 * @returns A proxy object where accessing any field returns its name as a string.
 *
 * Example Usage:
 * ```
 * const productFilter = new ProductFilter();
 * const fieldNames = extractFieldNames(productFilter);
 * console.log(fieldNames.page); // "page"
 * console.log(fieldNames.pageSize); // "pageSize"
 * ```
 */
export function extractFieldNames<T extends object>(
  obj: T,
): FieldNameExtractor<T> {
  return new Proxy(obj, {
    get(_, prop) {
      return prop;
    },
  }) as FieldNameExtractor<T>;
}

/**
 * Converts a set of enums into a mapping from enum values to human-readable labels.
 *
 * For each enum:
 * - Keys with underscores (`_`) are converted to spaces.
 * - Keys are converted to title case (e.g., "ENUM_VALUE" → "Enum Value").
 * - The word "and" (surrounded by spaces) is replaced with "&".
 * - Numeric keys and values equal to `0` (commonly representing "UNSPECIFIED") are ignored.
 * - Enums listed in `notFormatted` will skip label formatting and retain their original keys.
 *
 * @param enums - A record of enums (keyed by enum name).
 * @param options - Optional configuration.
 *   @param notFormatted - An array of enum names that should not be formatted.
 *
 * @returns A nested mapping: { [enumName]: { [enumValue]: formattedLabel } }
 */
export function generateProtoEnumMappings(
  enums: Record<string, EnumLike>,
  { notFormatted = [] }: { notFormatted?: string[] } = {},
): Record<string, Record<string | number, string>> {
  const isUnformattedEnum = (enumName: string) =>
    notFormatted.includes(enumName);

  const formatLabel = (key: string): string =>
    key
      .replace(/_/g, ' ') // Replace underscores with spaces
      .toLowerCase()
      .replace(/\band\b/g, '&') // Replace 'and' with '&'
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize each word

  const result: Record<string, Record<string | number, string>> = {};

  for (const [enumName, enumObj] of Object.entries(enums)) {
    const formattedEntries: Record<string | number, string> = {};

    for (const [key, value] of Object.entries(enumObj)) {
      if (isNaN(Number(key)) && value !== 0) {
        const label = isUnformattedEnum(enumName) ? key : formatLabel(key);
        formattedEntries[value] = label;
      }
    }

    result[enumName] = formattedEntries;
  }

  return result;
}

/**
 * Represents a mapping of enums with lowercase keys and numeric values.
 * - The outer keys represent the enum names (e.g., "Status", "Role").
 * - The inner keys are the lowercase enum keys.
 * - The inner values are the corresponding numeric enum values.
 */
type StringMappings = {
  [enumName: string]: { [enumKey: string]: number };
};

/**
 * Generates a mapping of enum keys (converted to lowercase) with their corresponding numeric values.
 *
 * - Only processes string-based keys.
 * - Skips numeric enum members (reverse-mapped values).
 *
 * @param enums - An object containing multiple enums.
 *   - The keys are enum names.
 *   - The values are objects mapping enum keys to enum values.
 *
 * @returns A structured `StringMappings` object where each enum key is lowercase
 *          and maps to its corresponding numeric value.
 */
export function generateLowercaseProtoEnumMappings(
  enums: Record<string, EnumLike>,
): StringMappings {
  return Object.entries(enums).reduce((acc, [enumName, enumObj]) => {
    acc[enumName] = Object.entries(enumObj).reduce(
      (enumAcc, [key, value]) => {
        // Ensure key is a string (not a numeric reverse mapping) and value is a number
        if (isNaN(Number(key)) && !isNaN(Number(value))) {
          enumAcc[key.toLowerCase()] = Number(value);
        }
        return enumAcc;
      },
      {} as Record<string, number>,
    );

    return acc;
  }, {} as StringMappings);
}

/**
 * Capitalizes the first letter of each word in a given string.
 *
 * @param value - The input string.
 * @returns The formatted string with each word capitalized.
 */
export function capitalizedPerWord(value: string) {
  return value
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Converts a payment amount to the smallest currency unit (e.g., cents for USD)
 * @param amount - The amount to convert.
 * @param factor - The conversion factor (default: 100 for most currencies).
 * @returns The amount in the smallest currency unit.
 */
export function convertPaymentAmountToCents(
  amount: number,
  factor: number = 100,
) {
  if (amount < 0) throw new Error('Payment amounts cannot be negative');
  if (!Number.isFinite(amount)) throw new Error('Invalid payment amount');

  return new Decimal(amount).times(factor).toNearest(1).toNumber();
}

/**
 * Checks if a given expiration timestamp has passed.
 *
 * @param expiresAt - The expiration timestamp in seconds (as a bigint).
 * @returns `true` if the current time is before or equal to the expiration timestamp, otherwise `false`.
 */
export function isExpired(expiresAt: bigint): boolean {
  const currentTimeStamp = BigInt(Math.floor(Date.now() / 1000)); // Convert to seconds safely
  return expiresAt >= currentTimeStamp;
}

/**
 * Retrieves the duration of a given video file in seconds.
 *
 * @param {File} file - The video file to check.
 * @returns {Promise<number>} A promise that resolves with the video's duration in seconds.
 *
 * The function creates a temporary video element, loads the metadata,
 * and extracts the duration. If an error occurs, the promise is rejected.
 */
export function getVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = URL.createObjectURL(file);
    video.onloadedmetadata = () => {
      resolve(video.duration);
      URL.revokeObjectURL(video.src);
    };
    video.onerror = () => {
      reject(new Error('Failed to retrieve video duration.'));
    };
  });
}

/**
 * `slugify` converts a string into a URL-friendly slug.
 * - Trims whitespace, converts to lowercase, and replaces non-allowed characters with dashes.
 * - Removes leading and trailing dashes.
 *
 * @param str The string to be slugified.
 * @returns The resulting slug.
 */
export function slugify(str: string): string {
  return str
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and') // replace ampersands with the word and
    .replace(/\s+/g, '-') // replace spaces with hyphens
    .replace(PARAMS_DISALLOWED_CHARACTERS, '') // remove disallowed characters
    .replace(/-+/g, '-') // collapse multiple hyphens
    .replace(/^-+|-+$/g, ''); // trim leading/trailing hyphens
}

/**
 * Checks if the current environment is a server.
 *
 * @returns {boolean} True if `window` is undefined.
 */
export function isServer(): boolean {
  return typeof window === 'undefined';
}

/**
 * Checks if the current environment is a client/browser.
 *
 * @returns {boolean} True if `window` is defined.
 */
export function isClient(): boolean {
  return typeof window !== 'undefined';
}

/**
 * Converts a string to camelCase.
 * Removes spaces, dashes, and underscores, and capitalizes each word after the first.
 *
 * @param {string} s - The input string to convert.
 * @returns {string} The camelCased version of the input.
 */
export function camelize(s: string): string {
  return s[0].toLowerCase() + s.slice(1);
}

/**
 * @param path - API route path (e.g. '/api/data')
 * @returns Full URL on server, path on client
 */
export function resolveApiUrl(path: string): string {
  if (path.startsWith(BASE_URL)) {
    throw new Error(
      `Invalid path: "${path}" already includes the base URL (${BASE_URL}).\n` +
        `Please provide only the route path (e.g. '/api/products') instead.`,
    );
  }

  if (isClient()) {
    return path;
  }

  return BASE_URL + path;
}
